import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import SEO from "@/components/SEO";
import DemoBooking from "@/components/DemoBooking";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import InteractiveAudioPlayer from "@/components/InteractiveAudioPlayer";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  company: z.string().min(2, "Company name is required").max(100),
  email: z.string().email("Please enter a valid email address").max(255),
  phone: z.string().optional(),
  useCase: z.string().min(1, "Please select a use case"),
  message: z.string().min(10, "Message must be at least 10 characters").max(1000),
});

type ContactForm = z.infer<typeof contactSchema>;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);

    try {
      // Integrate with FormSpree, EmailJS, or your email service
      // For now, we'll simulate the submission
      await new Promise(resolve => setTimeout(resolve, 1500));

      console.log("Form data:", data);

      toast({
        title: "Message Sent Successfully! ✉️",
        description: "We'll get back to you within 24 hours.",
      });

      reset();
    } catch (error) {
      toast({
        title: "Failed to Send",
        description: "Something went wrong. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <SEO
        title="Contact Holionix - Get in Touch with Our AI Voice Experts"
        description="Contact Holionix for enterprise AI voice solutions. Schedule a demo, get pricing, or speak with our sales team. Email, phone, and live chat support available."
      />
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-shadow-glow">
              Get in <span className="gradient-brand-text">Touch</span>
            </h1>
            <p className="text-xl text-foreground/80 max-w-2xl mx-auto mb-8">
              Ready to transform your customer service with AI? Let's talk about your needs.
            </p>
            
            <div className="flex justify-center gap-4 mb-8">
              <Button
                onClick={() => setShowBooking(false)}
                variant={!showBooking ? "default" : "outline"}
                className={!showBooking ? "gradient-brand text-white" : "border-accent text-accent hover:bg-accent/10"}
              >
                Send Message
              </Button>
              <Button
                onClick={() => setShowBooking(true)}
                variant={showBooking ? "default" : "outline"}
                className={showBooking ? "gradient-brand text-white" : "border-accent text-accent hover:bg-accent/10"}
              >
                Book a Demo
              </Button>
            </div>
          </motion.div>

          {showBooking ? (
            <DemoBooking />
          ) : (
            <div className="grid lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="card-dark gradient-border p-8">
                  <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        {...register("name")}
                        placeholder="John Doe"
                        className="mt-1"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="company">Company *</Label>
                      <Input
                        id="company"
                        {...register("company")}
                        placeholder="Acme Corporation"
                        className="mt-1"
                      />
                      {errors.company && (
                        <p className="text-red-500 text-sm mt-1">{errors.company.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="email">Work Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register("email")}
                        placeholder="john@acme.com"
                        className="mt-1"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        {...register("phone")}
                        placeholder="+49 123 456789"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="useCase">Primary Use Case *</Label>
                      <select
                        id="useCase"
                        {...register("useCase")}
                        className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/20"
                      >
                        <option value="">Select a use case</option>
                        <option value="inbound-support">Inbound Customer Support</option>
                        <option value="outbound-sales">Outbound Sales</option>
                        <option value="appointment-booking">Appointment Booking</option>
                        <option value="survey-feedback">Surveys & Feedback</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.useCase && (
                        <p className="text-red-500 text-sm mt-1">{errors.useCase.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        {...register("message")}
                        placeholder="Tell us about your needs..."
                        rows={5}
                        className="mt-1"
                      />
                      {errors.message && (
                        <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                      )}
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full gradient-brand text-white hover:opacity-90 h-12 text-base"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="mt-8 pt-8 border-t border-border/50">
                    <h3 className="font-bold mb-4 text-center">Or Talk to Our AI Agent Now</h3>
                    <InteractiveAudioPlayer 
                      agentName="Alex" 
                      agentRole="Demo Agent" 
                      avatar="/placeholder.svg"
                      accent="American English"
                      audioType="inbound-support"
                    />
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-6"
              >
                <Card className="card-dark gradient-border p-8 bg-gradient-to-br from-gradient-pink/10 to-gradient-purple/10">
                  <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Email</p>
                        <a href="mailto:contact@holionix.com" className="text-accent hover:underline">
                          info@holionix.com
                        </a>
                        <p className="text-sm text-foreground/60 mt-1">For general inquiries</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Phone</p>
                        <a href="tel:+49-15216724688" className="text-accent hover:underline">
                          +49 15216724688
                        </a>
                        <p className="text-sm text-foreground/60 mt-1">Monday-Friday, 9 AM - 6 PM CET</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Headquarters</p>
                        <p className="text-foreground/70">
                          Holionix GmbH<br />
                          Cologne, Germany<br />
                          North Rhine-Westphalia
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Response Time</p>
                        <p className="text-foreground/70">
                          We typically respond within 24 hours during business days
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark gradient-border p-8 text-center glow-effect">
                  <h3 className="text-xl font-bold mb-4">Strategic Partnerships</h3>
                  <p className="text-foreground/70 mb-4">
                    Interested in partnering with Holionix? We're always looking for strategic collaborations.
                  </p>
                  <Button variant="outline" className="border-accent text-accent hover:bg-accent/10">
                    Partner With Us
                  </Button>
                </Card>
              </motion.div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
